import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { extractString } from '../utils/sanitizeData';

export interface TagItem {
  id?: number | string;
  description?: string;
  name?: string;
  label?: string;
  color?: string;
}

export interface TagSuggestionsResponse {
  top_tags?: Array<string | TagItem>;
  recent_tags?: Array<string | TagItem>;
  top_used?: Array<string | TagItem>;
  recently_used?: Array<string | TagItem>;
  ai_suggestions?: Array<string | TagItem>;
  tags?: Array<string | TagItem>;
}

export interface UpdateInvoiceTagsPayload {
  tags: string[];
}

export interface UpdateInvoiceTagsResponse {
  tags: Array<string | TagItem>;
}

const FALLBACK_SUGGESTIONS: TagSuggestionsResponse = {
  top_tags: ['EV', 'Deslocações', 'Carregamento', 'Ferramentas&Utensílios', 'AI'],
  recent_tags: ['CHATGPT', 'Estacionamento', 'Portagens', 'Office', 'Cloud'],
  top_used: ['EV', 'Deslocações', 'Carregamento', 'Ferramentas&Utensílios', 'AI'],
  recently_used: ['CHATGPT', 'Estacionamento', 'Portagens', 'Office', 'Cloud'],
  ai_suggestions: ['IAPMEI', 'Cloud', 'Azure', 'Subscriptions'],
};

export function extractTagName(tag: string | TagItem | unknown): string {
  if (typeof tag === 'string') return tag.replace(/^#+/, '').trim();
  if (tag && typeof tag === 'object') {
    const obj = tag as TagItem;
    if (obj.description) return extractString(obj.description).replace(/^#+/, '').trim();
    if (obj.name) return extractString(obj.name).replace(/^#+/, '').trim();
    if (obj.label) return extractString(obj.label).replace(/^#+/, '').trim();
  }
  return extractString(tag, '').replace(/^#+/, '').trim();
}

/**
 * Hook to fetch tag autocomplete suggestions from GET /api/v2/tags/suggestions/
 */
export function useTagSuggestions() {
  return useQuery<TagSuggestionsResponse>({
    queryKey: ['tag-suggestions'],
    queryFn: async () => {
      try {
        const res = await api.get<TagSuggestionsResponse>('/v2/tags/suggestions/');
        return res.data;
      } catch {
        // Graceful fallback in local/offline environments
        return FALLBACK_SUGGESTIONS;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to save updated tags array to POST /api/v2/invoices/{public_token}/tags/
 */
export function useUpdateInvoiceTags() {
  const queryClient = useQueryClient();

  return useMutation<
    UpdateInvoiceTagsResponse,
    Error,
    { publicToken: string; tags: string[] }
  >({
    mutationFn: async ({ publicToken, tags }) => {
      const res = await api.post<UpdateInvoiceTagsResponse>(
        `/v2/invoices/${publicToken}/tags/`,
        { tags }
      );
      return res.data;
    },
    onSuccess: (data, { publicToken, tags }) => {
      // Invalidate and optimistically update invoice details & list
      queryClient.setQueryData(['invoice', publicToken, undefined], (old: Record<string, unknown> | undefined) => {
        if (!old) return old;
        return {
          ...old,
          tags: data?.tags || tags,
        };
      });
      queryClient.invalidateQueries({ queryKey: ['invoice', publicToken] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['tag-suggestions'] });
    },
  });
}
